import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, BadgeCheck, QrCode } from "lucide-react";
import Confetti from "../components/Confetti";

const LOGO = "/assets/brand/logo.png";
const GAME_LABELS = { roue: "Roue", grattage: "Grattage", memory: "Memory", runner: "Manicou Runner" };

/**
 * Page publique d'un bon (lien /v/{code}) : le client y voit son bon et peut
 * afficher son QR code pour le faire scanner en caisse. La page interroge le
 * statut ; dès que la caisse valide le bon, une animation de félicitations
 * s'affiche. La validation en boutique se fait côté /caisse (PIN équipe).
 */
export default function VoucherPage() {
  const { code } = useParams();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [justRedeemed, setJustRedeemed] = useState(false);
  const wasValidRef = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/voucher/${code}`);
      // Transition valide -> utilisé pendant que la page est ouverte = scan en caisse
      if (wasValidRef.current && data.status === "redeemed") setJustRedeemed(true);
      wasValidRef.current = data.status === "valid";
      setV(data); setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Bon introuvable");
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => { setLoading(true); await load(); if (alive) setLoading(false); })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Tant que le bon est valide, on interroge le statut pour détecter le scan caisse
  useEffect(() => {
    if (!v || v.status !== "valid") return;
    const id = setInterval(load, 3500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v?.status, code]);

  return (
    <div className="min-h-screen yum-bg flex flex-col items-center justify-center px-4"
      style={{ background: "radial-gradient(circle at 50% -10%,rgba(249,184,30,.12),transparent 55%),#241019" }}>
      <img src={LOGO} alt="Yüm World" className="w-36 mb-5 drop-shadow-xl" />
      <div className="bg-white rounded-3xl p-7 w-full max-w-sm shadow-2xl text-center yum-pop">
        {loading ? (
          <Loader2 className="animate-spin mx-auto text-[#E20613]" size={36} />
        ) : error ? (
          <><XCircle size={48} className="mx-auto text-gray-400 mb-2" /><p className="font-black text-gray-600">{error}</p></>
        ) : (
          <>
            <div className={`mx-auto mb-3 w-16 h-16 rounded-full flex items-center justify-center ${v.valid ? "bg-green-100" : "bg-amber-100"}`}>
              {v.valid ? <CheckCircle2 size={36} className="text-green-600" /> : <BadgeCheck size={36} className="text-amber-500" />}
            </div>
            <p className="text-xs text-gray-400 font-bold uppercase">Bon Yüm World · {GAME_LABELS[v.game] || v.game}</p>
            <p className="text-2xl font-black text-[#F26C1E] mt-1" data-testid="v-label">{v.label}</p>
            <p className="text-[#E20613] font-bold tracking-widest mt-1" data-testid="v-code">{v.code}</p>
            {(v.first_name || v.last_name) && <p className="text-sm text-gray-500 mt-1">{v.first_name} {v.last_name}</p>}
            <div className={`mt-3 inline-block px-4 py-1.5 rounded-full font-black text-sm ${
              v.status === "valid" ? "bg-green-100 text-green-700"
              : v.status === "expired" ? "bg-red-100 text-red-600"
              : "bg-gray-200 text-gray-500"}`} data-testid="v-status">
              {v.status === "valid" ? "✓ VALIDE" : v.status === "expired" ? "EXPIRÉ" : "DÉJÀ UTILISÉ"}
            </div>
            {v.expires_at && v.status === "valid" && (
              <p className="text-xs text-gray-400 mt-2">Valable jusqu'au {new Date(v.expires_at).toLocaleDateString("fr-FR")}</p>
            )}

            {v.status === "valid" && (
              <div className="mt-6">
                {!showQR ? (
                  <button
                    onClick={() => setShowQR(true)} data-testid="see-my-voucher"
                    className="flex items-center justify-center gap-2 w-full bg-[#E20613] text-white font-black py-3.5 rounded-2xl hover:bg-[#c40510] transition-colors"
                    style={{ boxShadow: "0 5px 0 #a81a0c" }}>
                    <QrCode size={20} /> Voir mon bon
                  </button>
                ) : (
                  <div className="yum-pop">
                    <img src={v.qr} alt="QR du bon" className="mx-auto w-52 h-52 rounded-2xl border-4 border-[#F9B81E]" data-testid="voucher-qr" />
                    <p className="text-sm font-bold text-gray-700 mt-3">Présente ce QR code en caisse pour le scan 📷</p>
                    <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-2">
                      <Loader2 className="animate-spin" size={13} /> En attente du scan…
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bon expiré ou déjà utilisé : on incite à rejouer + à créer un compte */}
            {v.status !== "valid" && (
              <div className="mt-6 border-t pt-5">
                <p className="text-sm text-gray-500 mb-3">
                  {v.status === "expired" ? "Oh non, ce bon a expiré 😢" : "Ce bon a déjà été utilisé ✓"}
                </p>
                <Link to="/" data-testid="replay-cta"
                  className="flex items-center justify-center gap-2 w-full bg-[#E20613] text-white font-black py-3.5 rounded-2xl hover:bg-[#c40510]"
                  style={{ boxShadow: "0 5px 0 #a81a0c" }}>
                  🎮 Rejouer pour gagner un nouveau bon
                </Link>
                <Link to="/compte" data-testid="account-cta"
                  className="mt-3 block rounded-2xl border-2 border-dashed border-[#F9B81E] bg-[#FFF8E8] px-4 py-3 text-left hover:bg-[#fff2d1]">
                  <p className="text-sm font-black text-gray-800 flex items-center gap-1.5">⭐ Crée un compte</p>
                  <p className="text-xs text-gray-500">Garde tes prochains bons <b className="text-[#E20613]">10 jours</b> au lieu de 5.</p>
                </Link>
              </div>
            )}
          </>
        )}
      </div>
      <Link to="/" className="mt-6 text-white/50 text-xs">Retour aux jeux</Link>

      {/* Félicitations : le bon vient d'être scanné en caisse */}
      {justRedeemed && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 px-6 text-center yum-pop" data-testid="redeem-celebration">
          <Confetti />
          <div className="text-7xl mb-4 animate-floaty">🎉</div>
          <h2 className="font-display text-white text-4xl font-black leading-none">Félicitations !</h2>
          <p className="text-white text-lg mt-3">pour l'utilisation de votre bon</p>
          <p className="font-script text-[#F9B81E] text-3xl mt-2">Régale-toi&nbsp;! 🧋🥞</p>
          <p className="text-white/70 text-sm mt-3 max-w-xs">Merci d'avoir joué chez YÜM World — reviens vite retenter ta chance&nbsp;!</p>
          <Link to="/" className="mt-7 inline-flex items-center gap-2 bg-[#F9B81E] text-[#1a1206] font-black px-8 py-3.5 rounded-full"
            style={{ boxShadow: "0 5px 0 #caa600" }}>Rejouer 🎮</Link>
        </div>
      )}
    </div>
  );
}
